import { ReactElement, useMemo } from "react";

import { Flex, Stack, Text } from "@chakra-ui/react";

import { QuotationLineModel } from "api";
import { ActiveSort, useRequestParams } from "contexts/RequestParamsProvider";
import { useContractTerms } from "hooks/api/contracts/useContractDetails";
import useSelectedContractDocEntry from "hooks/useSelectedContractDocEntry";
import { DEFAULT_EMPTY_VALUE, valueOr } from "util/statHelpers";
import BackToTopFAB from "components/BackToTopFAB";
import EmptyWithClearButton from "components/EmptyWithClearButton";
import LoadingSpinner from "components/LoadingSpinner";
import { BACK_TO_TOP_MIN_LINES } from "components/SortableTable/SortableDynamicTable";
import ContractDetailsFiltersSection, {
  detailsTabFilterCategories,
  FilterSectionStickyHeader,
} from "../filters/ContractDetailsFiltersSection";
import TermsTable from "./TermsTable";
import { Filter } from "components/Filters";
import ContractDetailsSelectsProvider from "contexts/ContractDetailsSelectsProvider";

const TermsPanel = ({
  unitOfMeasure: priceUnit = "ST",
  currency: currency,
  filterDefinitions,
}: Pick<Contract, "unitOfMeasure" | "currency"> & {
  filterDefinitions: Filter[];
}): ReactElement => {
  const { filters, activeSort, hasSelectedFilters, clearFilters } =
    useRequestParams();
  const selectedContractDocEntry = useSelectedContractDocEntry();

  const {
    data: terms,
    isLoading,
    isFetching,
  } = useContractTerms(
    selectedContractDocEntry,
    filters as OrdersFilters,
    activeSort as ActiveSort
  );
  const selectValues = useMemo(
    () =>
      terms?.value.map((value) => ({
        shipTo: value.shipTo as string | number,
        shipFrom: {
          label: value.shipFrom,
          value: value.shipFromCode,
        } as SelectOption,
        mode:
          typeof value.mode === "object"
            ? ({
                label: "name" in value.mode ? value.mode.name : "Unknown",
                value: "code" in value.mode ? value.mode.code : "Unknown",
              } as SelectOption)
            : DEFAULT_EMPTY_VALUE,
      })),
    [terms]
  );

  const termsTableEntries = useMemo(
    () =>
      terms?.value.map((value) => ({
        ...value,
        mode: valueOr((value.mode as Record<string, string | number>)?.name),
        price: `${value.price} ${currency}`,
      })) ?? [],
    [terms, currency]
  );

  const termsSortOptions: SortOption<QuotationLineModel>[] = useMemo(
    () => [
      {
        sortKey: "WarehouseCode",
        label: "Ship From",
      },
      {
        sortKey: "Region",
        label: "Ship To",
      },
      {
        sortKey: "ModeOfTransportationCode",
        label: "Mode",
      },
      {
        sortKey: "SalesTermsCode",
        label: "Sales Terms",
      },
      {
        sortKey: "Price",
        label: `Price/${priceUnit}`,
      },
    ],
    [priceUnit]
  );

  const termsList = useMemo(() => {
    if (!terms || isFetching || isLoading) {
      return <LoadingSpinner />;
    }

    return termsTableEntries.length > 0 ? (
      <BackToTopFAB
        isVisible={termsTableEntries.length > BACK_TO_TOP_MIN_LINES}
      >
        <TermsTable terms={termsTableEntries} priceUnit={priceUnit} />
      </BackToTopFAB>
    ) : (
      <EmptyWithClearButton
        message="No terms yet"
        hasFilters={hasSelectedFilters}
        onClick={clearFilters}
        buttonText="Clear all filters"
        buttonVisible={hasSelectedFilters}
      />
    );
  }, [
    terms,
    isFetching,
    isLoading,
    termsTableEntries,
    priceUnit,
    hasSelectedFilters,
    clearFilters,
  ]);

  return (
    <ContractDetailsSelectsProvider
      values={selectValues ?? []}
      selectMetadata={detailsTabFilterCategories}
    >
      <Stack spacing={0}>
        <FilterSectionStickyHeader>
          <Flex flex={1}>
            <Text textStyle="h4" color="grey">
              Terms
            </Text>
          </Flex>
          <ContractDetailsFiltersSection
            sortOptions={termsSortOptions}
            filterDefinitions={filterDefinitions}
          />
        </FilterSectionStickyHeader>
        {termsList}
      </Stack>
    </ContractDetailsSelectsProvider>
  );
};

export default TermsPanel;
